import { useEffect, useState } from "react";
import { MailSend, getMailSend } from "interfaces/emailing";
import { CategoryBadge, MailFrame, Modal, StatusBadge, effectiveStatus, formatDate, statusLabel } from "./parts";

/** One email exactly as the recipient got it (the HTML snapshot stored at send time), with its events. */
export default function SendDetail({ id, onClose }: { id: string; onClose: () => void }) {
    const [send, setSend] = useState<MailSend | null>(null);
    useEffect(() => {
        getMailSend(id).then(setSend).catch(() => setSend(null));
    }, [id]);
    return (
        <Modal wide title={send?.subject ?? "Email"} subtitle={send ? `À ${send.toName ? `${send.toName} <${send.to}>` : send.to} · ${formatDate(send.createdAt, true)}` : undefined} onClose={onClose}>
            {send && (
                <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-3">
                    <div className="space-y-4 overflow-y-auto border-border p-6 lg:border-r">
                        <div className="flex flex-wrap gap-2">
                            <StatusBadge status={effectiveStatus(send)} />
                            <CategoryBadge category={send.category} />
                        </div>
                        <dl className="space-y-2 text-sm">
                            <div>
                                <dt className="text-ink-muted">Modèle</dt>
                                <dd className="text-ink">{send.templateName}</dd>
                            </div>
                            <div>
                                <dt className="text-ink-muted">Envoyé par</dt>
                                <dd className="text-ink">{send.sentByEmail || "—"}</dd>
                            </div>
                            {send.replyTo && (
                                <div>
                                    <dt className="text-ink-muted">Réponses à</dt>
                                    <dd className="text-ink">{send.replyTo}</dd>
                                </div>
                            )}
                            {send.variables && Object.keys(send.variables).length > 0 && (
                                <div>
                                    <dt className="text-ink-muted">Variables saisies</dt>
                                    {Object.entries(send.variables).map(([k, v]) => (
                                        <dd key={k} className="text-ink">
                                            <span className="font-mono text-xs text-ink-muted">{k}</span> : {v}
                                        </dd>
                                    ))}
                                </div>
                            )}
                            {send.error && (
                                <div>
                                    <dt className="text-ink-muted">Erreur</dt>
                                    <dd className="text-danger">{send.error}</dd>
                                </div>
                            )}
                        </dl>
                        <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">Suivi</p>
                            <ol className="space-y-2 border-l border-border pl-4">
                                {/* A programmed send's createdAt is its departure time, not its creation: its own « scheduled » event says when it was programmed. */}
                                {[...(send.scheduledFor ? [] : [{ type: "queued", at: send.createdAt, detail: undefined as string | undefined }]), ...(send.events ?? [])].map((e, i) => (
                                    <li key={i} className="text-sm">
                                        <p className="text-ink">
                                            {e.type === "queued" ? "Créé" : e.type === "scheduled" ? `Programmé pour le ${formatDate(send.scheduledFor, true)} (heure de Paris)` : statusLabel(e.type)}
                                        </p>
                                        <p className="text-xs text-ink-muted">{formatDate(e.at, true)}</p>
                                        {e.detail && <p className="break-all text-xs text-ink-muted">{e.detail}</p>}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-col bg-canvas p-6 lg:col-span-2">
                        <MailFrame html={send.html} subject={send.subject} />
                    </div>
                </div>
            )}
        </Modal>
    );
}
